import React, { useState, useEffect } from 'react';
import { submissionsApi } from '../../api/Submission';
import {
  SpaceBetween,
  Container,
  Box,
  ProgressBar,
  Spinner,
  Header,
  TextContent,
  Alert,
  Badge,
  StatusIndicator
} from '@cloudscape-design/components';
import { parseAiRecommendationPercentageScore } from '../../common/utility'

const RecommendationsList = ({ recommendations }) => {
  if (!recommendations || !recommendations.description_recommendations || !recommendations.admin_recommendations) {
    return <Box>No recommendations available</Box>;
  }

  return (
    <SpaceBetween size="m">
      {recommendations.description_recommendations.map((rec, idx) => (
        <Container key={`desc-${idx}`}>
          <Box variant="p" fontWeight="bold">• {rec.point}</Box>
          <Box variant="p" color="text-body-secondary">{rec.analysis}</Box>
        </Container>
      ))}
      
      {recommendations.admin_recommendations.map((rec, idx) => (
        <Container key={`admin-${idx}`}>
          <Box variant="p" fontWeight="bold">• {rec.point}</Box>
          <Box variant="p" color="text-body-secondary">{rec.analysis}</Box>
        </Container>
      ))}
    </SpaceBetween>
  );
};

const IndicatorCard = ({ indicator }) => {
  const hasRecommendations = indicator.latest_recommendation?.details?.recommendations;
  const score = indicator.latest_recommendation?.score;
  const displayScore = (score ?? null) === null ? "n/a" : `${parseAiRecommendationPercentageScore(score)}%`;

  return (
    <Container
      header={
        <Header 
          variant="h3"
          info={
            <Box float="right">
              <Badge color="blue">
                AI Score: {displayScore}
              </Badge>
            </Box>
          }
        >
          {indicator.indicator_index}: {indicator.indicator_name || 'Unnamed Indicator'}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Show recommendations if they exist */}
        {hasRecommendations && (
          <Box>
            <Box variant="awsui-key-label">AI Recommendations:</Box>
            <RecommendationsList recommendations={indicator.latest_recommendation.details.recommendations} />
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

const MidSectionComponent = ({ midSection }) => {
  return (
    <Container
      header={
        <Header variant="h2">
          {midSection.title}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {midSection.indicator_submissions?.map(indicator => (
          <IndicatorCard key={indicator.indicator_submission_id} indicator={indicator} />
        ))}
      </SpaceBetween>
    </Container>
  );
};

const AspectSection = ({ aspect }) => {
  return (
    <Container
      header={
        <Header variant="h1">
          {aspect.title}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {aspect.mid_sections?.map(midSection => (
          <MidSectionComponent key={midSection.id} midSection={midSection} />
        ))}
        {aspect.indicator_submissions?.map(indicator => (
          <IndicatorCard key={indicator.indicator_submission_id} indicator={indicator} />
        ))}
      </SpaceBetween>
    </Container>
  );
};

export default function SubmissionDetails({ applicationID }) {
  const [frameworkData, setFrameworkData] = useState(null);
  const [applicationScore, setApplicationScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await submissionsApi.getStandardsIndicators(applicationID);
        setApplicationScore(data.avg_score)
        setFrameworkData(data);
      } catch (err) {
        setError(err);
        console.error('Error fetching submission data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [applicationID]);

  if (loading) {
    return (
      <Box textAlign="center" padding={{ top: "xxl" }}>
        <Spinner size="large" />
        <Box variant="h2" padding={{ top: "s" }}>Loading submission details...</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" padding={{ top: "xxl" }}>
        <StatusIndicator type="error">
          Error loading submission details
        </StatusIndicator>
      </Box>
    );
  }

  const renderContent = () => {
    if (!frameworkData) return null;

    const { framework, aspects } = frameworkData;

    if (framework.id === 1) {
      // Framework 1: Only show mid_sections (standards), ignore aspects
      const allMidSections = aspects?.flatMap(aspect => aspect.mid_sections || []) || [];
      return allMidSections.map(midSection => (
        <MidSectionComponent key={midSection.id} midSection={midSection} />
      ));
    } else {
      // Framework 2: Show full hierarchy (aspects → mid_sections)
      return aspects?.map(aspect => (
        <AspectSection key={aspect.id} aspect={aspect} />
      )) || [];
    }
  };

  return (
    <SpaceBetween size="l">
      <Alert type="info">
      The following is a summary of the AI findings related to the completeness of your submission. It is highly recommended that you address the following recommendations before finalizing your submission through the Review Management System (RMS).       </Alert>
      <Box float="left">
        <Badge color="blue">
        Application AI Score:  {applicationScore ? `${parseAiRecommendationPercentageScore(applicationScore)}%` : "n/a" }
        </Badge>
      </Box>
      {renderContent()}
    </SpaceBetween>
  );
}